import { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import { useCashSession } from '@/hooks/useCashSession';
import { useSalesSettings } from '@/hooks/useSalesSettings';
import { friendlyError } from '@/lib/errors';
import { formatMoney } from '@/lib/format';
import {
  yesterdayStr,
  todayStr,
  formatPeriodLabel,
  differenceLabel,
  round2,
  type CashPeriodMode,
} from '@/lib/cashSession';
import { printCashSession } from '@/services/print/printCashSession';
import SummarySection from '@/pages/cash/components/SummarySection';
import PaymentBreakdownCard from '@/pages/cash/components/PaymentBreakdownCard';
import CashMovementsCard from '@/pages/cash/components/CashMovementsCard';
import CloseSessionModal from '@/pages/cash/components/CloseSessionModal';
import SessionHistory from '@/pages/cash/components/SessionHistory';
import PeriodFilter from '@/pages/cash/components/PeriodFilter';
import type { CashSession } from '@/lib/types';

export default function CashRegisterPage() {
  const { user, isAdmin } = useAuth();
  const { settings } = useSalesSettings();

  const [mode, setMode] = useState<CashPeriodMode>('today');
  const [customDate, setCustomDate] = useState(todayStr());
  const [cashCounted, setCashCounted] = useState('');
  const [observations, setObservations] = useState('');
  const [closeOpen, setCloseOpen] = useState(false);
  const [processing, setProcessing] = useState(false);
  const [toast, setToast] = useState<string | null>(null);
  const [refreshKey, setRefreshKey] = useState(0);

  const periodDate = useMemo(() => {
    if (mode === 'yesterday') return yesterdayStr();
    if (mode === 'custom') return customDate || todayStr();
    return todayStr();
  }, [mode, customDate]);

  const currencySymbol = settings?.currency_symbol ?? 'RD$';
  const {
    session,
    summary,
    movements,
    loading,
    error,
    branch,
    branchId,
    userName,
    roleLabel,
    canUse,
    isClosed,
    reload,
    addMovement,
    removeMovement,
    closeSession,
  } = useCashSession(periodDate);

  const showToast = (msg: string) => {
    setToast(msg);
    setTimeout(() => setToast(null), 2600);
  };

  const countedValue = cashCounted.trim() === '' ? null : Number(cashCounted);
  const difference = countedValue == null ? null : round2(countedValue - summary.cashExpected);
  const diffMeta = difference == null ? null : differenceLabel(difference);

  const handleClose = async () => {
    if (countedValue == null) {
      showToast('Ingresa el efectivo contado.');
      setCloseOpen(false);
      return;
    }
    setProcessing(true);
    const res = await closeSession(countedValue, observations);
    setProcessing(false);
    setCloseOpen(false);
    if (res.error) {
      showToast(res.error);
      return;
    }
    setRefreshKey((k) => k + 1);
    setCashCounted('');
    showToast('Cuadre cerrado correctamente.');
  };

  const handlePrint = async (target: CashSession) => {
    const result = await printCashSession({ branch, session: target, currencySymbol });
    if (result.message && !result.success) showToast(result.message);
  };

  return (
    <div className="space-y-5">
      {/* Encabezado */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <div>
          <h1 className="font-heading text-2xl md:text-3xl font-bold text-foreground-950">
            Cuadre de caja
          </h1>
          <div className="mt-1 flex flex-wrap items-center gap-2 text-sm text-foreground-600">
            <span className="inline-flex items-center gap-1.5">
              <i className="ri-user-3-line" /> Responsable: {userName}
            </span>
            <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-background-100 text-xs font-medium text-foreground-700">
              {roleLabel}
            </span>
            {branch && (
              <span className="inline-flex items-center gap-1.5">
                <i className="ri-building-2-line" /> {branch.name}
              </span>
            )}
          </div>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <Link
            to="/reports/ganancias"
            className="inline-flex items-center gap-2 rounded-md border border-background-300 px-3 py-2 text-sm font-medium text-foreground-700 hover:bg-background-100 whitespace-nowrap transition cursor-pointer"
          >
            <i className="ri-funds-line" /> Ganancias
          </Link>
          {isAdmin && (
            <Link
              to="/cash/supervision"
              className="inline-flex items-center gap-2 rounded-md border border-background-300 px-3 py-2 text-sm font-medium text-foreground-700 hover:bg-background-100 whitespace-nowrap transition cursor-pointer"
            >
              <i className="ri-folder-shield-2-line" /> Supervisar cuadres
            </Link>
          )}
          {session && (
            <button
              onClick={() => handlePrint(session)}
              className="inline-flex items-center gap-2 rounded-md border border-background-300 px-3 py-2 text-sm font-medium text-foreground-700 hover:bg-background-100 whitespace-nowrap transition cursor-pointer"
            >
              <i className="ri-printer-line" /> Imprimir cuadre
            </button>
          )}
        </div>
      </div>

      <PeriodFilter
        mode={mode}
        customDate={customDate}
        onChangeMode={setMode}
        onChangeCustomDate={setCustomDate}
      />

      {isClosed && (
        <div className="rounded-md border border-secondary-200 bg-secondary-100 px-4 py-3 text-sm text-secondary-900 flex items-center gap-2">
          <i className="ri-lock-2-line" />
          Este cuadre está <strong>CERRADO</strong>. Cerrado el {formatPeriodLabel(periodDate)}.
        </div>
      )}

      {!canUse ? (
        <div className="rounded-lg border border-dashed border-background-300 p-10 text-center">
          <i className="ri-building-2-line text-4xl text-foreground-400" />
          <p className="mt-3 text-foreground-600">Selecciona una sucursal para ver tu cuadre.</p>
        </div>
      ) : loading ? (
        <div className="flex items-center justify-center py-20">
          <div className="w-10 h-10 rounded-full border-2 border-primary-500 border-t-transparent animate-spin" />
        </div>
      ) : error ? (
        <div className="rounded-lg border border-accent-200 bg-accent-50 p-6 text-center">
          <i className="ri-error-warning-line text-4xl text-accent-700" />
          <p className="mt-3 text-foreground-800">{friendlyError(error, 'No se pudo cargar el cuadre.')}</p>
          <button
            onClick={reload}
            className="mt-4 inline-flex items-center gap-2 rounded-md bg-primary-500 hover:bg-primary-600 px-4 py-2.5 text-sm font-semibold text-background-50 whitespace-nowrap transition cursor-pointer"
          >
            <i className="ri-refresh-line" /> Reintentar
          </button>
        </div>
      ) : (
        <>
          <SummarySection summary={summary} currencySymbol={currencySymbol} />
          <PaymentBreakdownCard breakdown={summary.breakdown} currencySymbol={currencySymbol} />

          <CashMovementsCard
            movements={movements}
            currencySymbol={currencySymbol}
            disabled={isClosed}
            onAdd={addMovement}
            onRemove={removeMovement}
          />

          {/* Efectivo y cierre */}
          <div className="rounded-lg border border-background-200 bg-background-50 p-5">
            <h2 className="font-heading text-lg font-semibold text-foreground-950">
              Efectivo y cierre
            </h2>

            <div className="mt-4 grid grid-cols-1 lg:grid-cols-2 gap-5">
              <div className="space-y-3">
                <div className="rounded-lg border border-background-200 bg-background-100/60 p-4">
                  <p className="text-xs font-medium text-foreground-500">Efectivo esperado</p>
                  <p className="mt-1 font-heading text-2xl font-bold text-foreground-950">
                    {formatMoney(summary.cashExpected, currencySymbol)}
                  </p>
                  <p className="mt-1 text-xs text-foreground-500">
                    Ventas en efectivo {formatMoney(summary.cashSales, currencySymbol)} + entradas{' '}
                    {formatMoney(summary.cashIn, currencySymbol)} − retiros{' '}
                    {formatMoney(summary.cashOut, currencySymbol)} − devoluciones{' '}
                    {formatMoney(summary.returnsTotal, currencySymbol)}
                  </p>
                </div>

                <div>
                  <label className="block text-sm font-medium text-foreground-800 mb-1.5">
                    Efectivo contado
                  </label>
                  <div className="relative max-w-[260px]">
                    <span className="absolute left-3 top-1/2 -translate-y-1/2 text-sm text-foreground-500">
                      {currencySymbol}
                    </span>
                    <input
                      type="number"
                      min="0"
                      step="0.01"
                      value={cashCounted}
                      onChange={(e) => setCashCounted(e.target.value)}
                      disabled={isClosed}
                      placeholder="0.00"
                      className="w-full rounded-md border border-background-300 bg-background-50 pl-12 pr-3 py-2.5 text-sm text-foreground-900 focus:outline-none focus:ring-2 focus:ring-primary-400 disabled:opacity-60"
                    />
                  </div>
                </div>

                {difference != null && diffMeta && (
                  <div
                    className={`rounded-lg border p-4 ${
                      diffMeta.kind === 'ok'
                        ? 'border-accent-200 bg-accent-50'
                        : 'border-primary-200 bg-primary-50'
                    }`}
                  >
                    <p className="text-xs font-medium text-foreground-500">Diferencia</p>
                    <p className="mt-0.5 font-heading text-xl font-bold text-foreground-950">
                      {formatMoney(difference, currencySymbol)}
                    </p>
                    <p
                      className={`mt-1 text-sm font-semibold ${
                        diffMeta.kind === 'ok' ? 'text-accent-800' : 'text-primary-800'
                      }`}
                    >
                      {diffMeta.kind === 'ok'
                        ? 'CUADRE CORRECTO'
                        : `${diffMeta.title}: ${formatMoney(Math.abs(difference), currencySymbol)}`}
                    </p>
                  </div>
                )}
              </div>

              <div className="space-y-3">
                <div>
                  <label className="block text-sm font-medium text-foreground-800 mb-1.5">
                    Observación del cuadre {difference != null && diffMeta?.kind !== 'ok' ? '(recomendada)' : '(opcional)'}
                  </label>
                  <textarea
                    value={observations}
                    onChange={(e) => setObservations(e.target.value)}
                    disabled={isClosed}
                    maxLength={500}
                    rows={5}
                    placeholder="Ej: Se entregaron RD$200 para cambio o faltante detectado al contar la caja."
                    className="w-full rounded-md border border-background-300 bg-background-50 px-3 py-2.5 text-sm text-foreground-900 focus:outline-none focus:ring-2 focus:ring-primary-400 disabled:opacity-60 resize-none"
                  />
                </div>

                {!isClosed ? (
                  <button
                    onClick={() => setCloseOpen(true)}
                    disabled={countedValue == null}
                    className="w-full inline-flex items-center justify-center gap-2 rounded-md bg-primary-500 hover:bg-primary-600 px-4 py-3 text-sm font-semibold text-background-50 whitespace-nowrap transition disabled:opacity-60 cursor-pointer"
                  >
                    <i className="ri-lock-line" /> Cierre de caja
                  </button>
                ) : (
                  <div className="rounded-md border border-background-200 bg-background-100/60 p-4 text-sm text-foreground-600">
                    <p className="flex items-center gap-2 font-medium text-foreground-800">
                      <i className="ri-checkbox-circle-line text-accent-700" /> Cuadre cerrado
                    </p>
                    {session?.observations && (
                      <p className="mt-2 text-foreground-700">Observación: {session.observations}</p>
                    )}
                    <button
                      onClick={() => session && handlePrint(session)}
                      className="mt-3 inline-flex items-center gap-2 rounded-md border border-background-300 px-3 py-2 text-sm font-medium text-foreground-700 hover:bg-background-100 whitespace-nowrap transition cursor-pointer"
                    >
                      <i className="ri-printer-line" /> Reimprimir cuadre
                    </button>
                  </div>
                )}
              </div>
            </div>
          </div>

          {user?.id && (
            <SessionHistory
              userId={user.id}
              branchId={branchId}
              refreshKey={refreshKey}
              currencySymbol={currencySymbol}
              onPrint={handlePrint}
            />
          )}

          <CloseSessionModal
            open={closeOpen}
            onClose={() => setCloseOpen(false)}
            onConfirm={handleClose}
            processing={processing}
            summary={summary}
            cashCounted={countedValue ?? 0}
            currencySymbol={currencySymbol}
            userName={userName}
            periodLabel={formatPeriodLabel(periodDate)}
          />
        </>
      )}

      {toast && (
        <div className="fixed bottom-20 lg:bottom-6 left-1/2 -translate-x-1/2 z-[60] rounded-md bg-foreground-950 text-background-50 px-4 py-2.5 text-sm font-medium">
          {toast}
        </div>
      )}
    </div>
  );
}